import {
  notify,
  notifyNetworkErrorThrottled,
  notifyServerErrorThrottled,
} from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@/lib/storeManager";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import { toUserFacingError } from "@/lib/humanizeApiError";
import axios from "axios";

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  // Don't add Authorization header for refresh token, login/OTP, or public contact form
  const isAuthLoginOrOtp =
    config.url === "/api/auth" ||
    config.url === "/api/auth/" ||
    config.url?.includes("/api/auth/login/");
  if (
    token &&
    config.headers &&
    !config.url?.includes("/api/auth/token-refresh/") &&
    !isAuthLoginOrOtp
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const resolvedTenantId =
    tenantId ??
    (import.meta.env.VITE_X_TENANTID ?? "")
      .toString()
      .replace(/^"|"$/g, "")
      .trim();

  if (resolvedTenantId && config.headers) {
    config.headers["X-Tenant-Id"] = resolvedTenantId;
  } else if (import.meta.env.DEV) {
    console.warn("No tenantId found for API request:", config.url);
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/** Many parallel requests can return the same 4xx message — avoid toast spam */
let last4xxToastAt = 0;
let last4xxMessage = "";
const DUPLICATE_CLIENT_ERROR_COOLDOWN_MS = 8000;

function shouldSkipDuplicateClientErrorToast(message: unknown): boolean {
  const msg = String(message ?? "");
  const now = Date.now();
  if (
    msg === last4xxMessage &&
    now - last4xxToastAt < DUPLICATE_CLIENT_ERROR_COOLDOWN_MS
  ) {
    return true;
  }
  last4xxMessage = msg;
  last4xxToastAt = now;
  return false;
}

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url ?? "";
    const errorStatus = error.response?.status;

    const shouldHideMessage =
      hidenMessageAPI.some(path => requestUrl.includes(path)) ||
      originalRequest?.silent === true;

    // No HTTP response: offline, DNS, CORS, connection limits, server unreachable.
    // Handle early so we do not fall through to generic notify + duplicate toast from axiosUitils.
    const isNetworkLayerFailure =
      !error.response &&
      (error.code === "ERR_NETWORK" ||
        error.code === "ECONNABORTED" ||
        error.message === "Network Error" ||
        String(error.message ?? "").includes("Network Error"));

    if (isNetworkLayerFailure) {
      if (!shouldHideMessage) {
        notifyNetworkErrorThrottled();
      }
      return Promise.reject(error);
    }

    // Get error message from various possible locations
    const errorMessage =
      error.response?.data?.message ??
      error.response?.data?.error?.message ??
      error.response?.data?.error ??
      error.response?.data?.detail ??
      error.message ??
      "An error occurred";

    const errorMessageLower =
      typeof errorMessage === "string" ? errorMessage.toLowerCase() : "";

    // Suppress "Authentication credentials were not provided" errors
    const isAuthCredentialsError =
      errorMessageLower.includes("authentication credentials") &&
      errorMessageLower.includes("not provided");

    // Suppress "Authentication credentials were not provided" errors
    if (isAuthCredentialsError) {
      console.warn(
        "⚠️ Suppressed 'Authentication credentials were not provided' error:",
        errorMessage,
        "Status:",
        errorStatus,
        "URL:",
        requestUrl
      );
      // Don't show toast, just reject silently
      return Promise.reject(error);
    }

    if (errorStatus === 400 || errorStatus === 404 || errorStatus === 417) {
      // Suppress "Column not found in schema" errors - these are expected when schema JSON has fields that don't exist in DB
      if (
        typeof errorMessage === "string" &&
        (errorMessage.toLowerCase().includes("column not found") ||
          errorMessage.toLowerCase().includes("column not found in schema"))
      ) {
        console.warn(
          "⚠️ Suppressed 'Column not found' error (expected when schema JSON has fields not in DB):",
          errorMessage
        );
        return Promise.reject(error);
      }

      // Suppress "Authentication credentials were not provided" errors
      if (isAuthCredentialsError) {
        console.warn(
          "⚠️ Suppressed 'Authentication credentials were not provided' error (400/404/417):",
          errorMessage
        );
        return Promise.reject(error);
      }

      // Suppress global notify for login/OTP errors - page shows single "Invalid OTP" toast
      const isAuthOtpEndpoint =
        requestUrl === "/api/auth" ||
        requestUrl === "/api/auth/" ||
        requestUrl?.includes("/api/auth/login/");
      if (isAuthOtpEndpoint) {
        return Promise.reject(error);
      }

      // Suppress messages for APIs in hidenMessageAPI
      if (!shouldHideMessage) {
        if (!shouldSkipDuplicateClientErrorToast(errorMessage)) {
          notify.error(toUserFacingError(errorMessage));
        }
      }
      return Promise.reject(error);
    }

    // Handle 500 errors and other status codes
    if (errorStatus === 500 || errorStatus >= 500) {
      // Suppress authentication credentials errors on 500 errors too
      if (isAuthCredentialsError) {
        console.warn(
          "⚠️ Suppressed 500 error with 'Authentication credentials were not provided' message:",
          errorMessage
        );
        return Promise.reject(error);
      }

      // One toast per burst when many parallel calls return 5xx
      if (!shouldHideMessage) {
        if (import.meta.env.DEV) {
          console.warn("[API] 5xx:", requestUrl, errorMessage);
        }
        notifyServerErrorThrottled();
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshToken();
        const newAccessToken = response.access;
        setLocalAccessToken(newAccessToken);
        if (response.refresh) {
          setLocalRefreshToken(response.refresh);
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      // This is the fallback for any other errors (not 400, 404, 417, 401, 500)
      // Check again for password errors on user updates before showing toast
      const fallbackErrorMessage =
        error.response?.data?.error?.message ??
        error.response?.data?.message ??
        error.response?.data?.error ??
        error.message ??
        "An error occurred";

      const fallbackErrorMessageLower =
        typeof fallbackErrorMessage === "string"
          ? fallbackErrorMessage.toLowerCase()
          : "";
      const isAuthCredentialsErrorFallback =
        fallbackErrorMessageLower.includes("authentication credentials") &&
        fallbackErrorMessageLower.includes("not provided");

      // Suppress authentication credentials errors in fallback handler too
      if (isAuthCredentialsErrorFallback) {
        console.warn(
          "⚠️ Suppressed 'Authentication credentials were not provided' error in fallback handler:",
          fallbackErrorMessage
        );
        return Promise.reject(error);
      }

      if (!shouldHideMessage) {
        if (!shouldSkipDuplicateClientErrorToast(fallbackErrorMessage)) {
          notify.error(toUserFacingError(fallbackErrorMessage));
        }
      }
      return Promise.reject(error);
    }
  }
);

const refreshToken = async () => {
  const response = await axios.post(
    `${import.meta.env.VITE_API_BASE_URL}/api/auth/token-refresh/`,
    {
      refresh: getRefreshToken() ?? "",
    },
    {
      headers: {
        "X-Tenant-Id": getTenantId(),
        Authorization: `Bearer ${getAccessToken() ?? ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
